// The search pop-up's list: what is drawn, in which order, and which rows the arrow
// keys walk. A pure function of what was typed, the page results and where the server's
// part stands, so the rule that matters can be tested without a screen:
//
//   ROWS ALREADY SHOWN NEVER MOVE. Page results are worked out in the browser and are
//   there at once; they come first and keep their places. What the server finds is only
//   ever added below them, all of it together, for the text it was asked for. The
//   highlighted row is a position in this list, so it stays on the row the person saw.
//
// While the server is asked too, the pages take a few rows and no more, so that what it
// finds, and the line that says it is searching or could not, are on screen without
// scrolling. The cut depends on the text and the project alone, never on whether the
// answer has arrived, so it is the same before and after and the rule above still holds.
import type { ThingGroup, ThingKind, ThingRow } from '../services/searchApi';
import type { SearchEntry } from './searchIndex';

/** Where the server's part stands (components/shell/useSearchResults). */
export type ServerPart =
  | { status: 'off' }
  | { status: 'waiting' }
  | { status: 'ready'; groups: ThingGroup[] }
  | { status: 'failed' };

export const GROUP_HEADING: Record<ThingKind, string> = {
  person: 'People',
  store: 'Stores',
  task: 'Tasks',
  training: 'Training',
  product: 'Products',
  dashboard: 'Dashboards',
  report: 'Reports',
};

/** How many page rows are drawn while the server is asked too, until the person asks for all of them. */
export const PAGE_ROWS_SHOWN = 4;

export const WORDS = {
  pages: 'Pages',
  mostOpened: 'Pages you open most',
  /** The row that brings back the page rows held back while the server is asked too. */
  allPages: (count: number) => `All ${count} pages`,
  searching: 'Searching…',
  groupFailed: 'Could not search.',
  failed: 'Could not search people and stores.',
  tryAgain: 'Try again',
  more: 'Type more to narrow.',
  nothing: (text: string) => `Nothing found for "${text}".`,
  /** Only pages were searched: under three letters, or no project selected. */
  noPage: (text: string) => `No page matches "${text}".`,
} as const;

/** A row a person can pick: with the arrow keys and Enter, or with a click. */
export type SearchRow =
  | { type: 'page'; key: string; entry: SearchEntry }
  | { type: 'thing'; key: string; kind: ThingKind; thing: ThingRow; to: string }
  /** Shows every page row for this text. `count` is how many there are. */
  | { type: 'allPages'; key: string; count: number }
  | { type: 'retry'; key: string };

export interface SearchBlock {
  key: string;
  /** The small heading over the block; none over the "could not search" block. */
  heading: string | null;
  /** A sentence before the rows. */
  lead: string | null;
  /** The rows, each with its position in `SearchLayout.rows`. */
  rows: Array<{ row: SearchRow; index: number }>;
}

export interface SearchLayout {
  blocks: SearchBlock[];
  /** Every row that can be picked, in the order drawn: what the arrow keys walk. */
  rows: SearchRow[];
  /** One grey line under everything: still searching, nothing found, or more found than is listed. */
  line: string | null;
}

/**
 * The grey words beside a thing's name: its code, then its detail. The code is left out
 * when it is the name (a person our tables hold no name for is listed by their code).
 */
export function greyLine(thing: Pick<ThingRow, 'name' | 'detail' | 'code'>): string {
  const code = (thing.code ?? '').trim();
  const detail = thing.detail.trim();
  return [code && code !== thing.name.trim() ? code : '', detail].filter(Boolean).join(' · ');
}

export interface SearchLayoutInput {
  /** What is in the box, trimmed. */
  typed: string;
  /** The page results for it, or the pages the person opens most when nothing is typed. */
  pageRows: SearchEntry[];
  /** The person asked for every page row for this text ("All N pages"). */
  allPages: boolean;
  /**
   * Its `off` is decided by the text and the project alone: under three letters, or no
   * project selected. The page rows are cut whenever it is anything else.
   */
  server: ServerPart;
  /** Where a found thing opens; null when it cannot be opened, and it is then not listed. */
  destination: (kind: ThingKind, thing: ThingRow) => string | null;
}

export function layoutSearch({ typed, pageRows, allPages, server, destination }: SearchLayoutInput): SearchLayout {
  const blocks: SearchBlock[] = [];
  const rows: SearchRow[] = [];
  const place = (row: SearchRow) => {
    rows.push(row);
    return { row, index: rows.length - 1 };
  };

  if (pageRows.length > 0) {
    // Uncapped, a word like "store" fills the list with two-line page rows, and the
    // people and stores found, "Searching…" and "Could not search" all sit below the
    // fold. Cut by `off` and nothing else about the server's part, so the same rows are
    // drawn while it is asked and once it has answered.
    const held = server.status !== 'off' && !allPages && pageRows.length > PAGE_ROWS_SHOWN;
    const pageBlockRows = (held ? pageRows.slice(0, PAGE_ROWS_SHOWN) : pageRows).map((entry) => place({ type: 'page', key: entry.id, entry }));
    // One row brings the rest back, counting every page so the number is what opening it shows.
    if (held) pageBlockRows.push(place({ type: 'allPages', key: 'all-pages', count: pageRows.length }));
    blocks.push({ key: 'pages', heading: typed ? WORDS.pages : WORDS.mostOpened, lead: null, rows: pageBlockRows });
  }

  // A drawn group holds more than it lists.
  let cutShort = false;
  if (server.status === 'ready') {
    const drawn = new Set<ThingKind>();
    for (const group of server.groups) {
      // One block a kind, whatever the answer holds: a second would repeat a heading.
      if (drawn.has(group.kind)) continue;
      const heading = GROUP_HEADING[group.kind];
      if (group.status === 'failed') {
        drawn.add(group.kind);
        blocks.push({ key: group.kind, heading, lead: WORDS.groupFailed, rows: [] });
        continue;
      }
      const things: SearchRow[] = [];
      for (const thing of group.rows) {
        const to = destination(group.kind, thing);
        if (to) things.push({ type: 'thing', key: `${group.kind}:${thing.id}`, kind: group.kind, thing, to });
      }
      // A group with nothing in it is not drawn.
      if (things.length === 0) continue;
      drawn.add(group.kind);
      if (group.more) cutShort = true;
      blocks.push({ key: group.kind, heading, lead: null, rows: things.map(place) });
    }
  } else if (server.status === 'failed') {
    // "Try again" is a row, not a button: the pop-up has two Tab stops, the box and
    // Close, and a row is reached with the arrow keys like everything else in the list.
    blocks.push({ key: 'failed', heading: null, lead: WORDS.failed, rows: [place({ type: 'retry', key: 'retry' })] });
  }

  let line: string | null = null;
  if (server.status === 'waiting') {
    line = WORDS.searching;
  } else if (typed && blocks.length === 0) {
    // Nothing drawn at all. "Nothing found" is said only when the server was asked and
    // answered with no failed group (a failed group is a block, so it is not this case).
    line = server.status === 'ready' ? WORDS.nothing(typed) : WORDS.noPage(typed);
  } else if (cutShort) {
    // Said once, after the last group: under each cut group it was the same sentence
    // several times in one list.
    line = WORDS.more;
  }

  return { blocks, rows, line };
}
